import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/branding")({
  component: BrandingPage,
  head: () => ({ meta: [{ title: "Branding — MissionReady" }] }),
});

function BrandingPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const canEdit = isPlatformAdmin || currentRole === "org_owner" || currentRole === "admin";
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [primary, setPrimary] = useState("#CC0000");
  const [accent, setAccent] = useState("#FF6B35");
  const [subdomain, setSubdomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [logoPath, setLogoPath] = useState<string | null>(null);

  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    supabase.from("org_branding").select("*").eq("organization_id", currentOrg.id).maybeSingle()
      .then(async ({ data }) => {
        if (cancelled || !data) return;
        setPrimary(data.primary_color ?? "#CC0000");
        setAccent(data.accent_color ?? "#FF6B35");
        setSubdomain(data.subdomain ?? "");
        const path = data.logo_url;
        setLogoPath(path ?? null);
        if (path && !path.startsWith("http")) {
          const { data: signed } = await supabase.storage.from("org-branding").createSignedUrl(path, 3600);
          if (!cancelled) setLogoUrl(signed?.signedUrl ?? null);
        } else {
          setLogoUrl(path ?? null);
        }
      });
    return () => { cancelled = true; };
  }, [currentOrg?.id]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentOrg) return;
    setUploading(true);
    const ext = file.name.split(".").pop() || "png";
    const path = `${currentOrg.id}/logo-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("org-branding").upload(path, file, { upsert: true });
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data: signed } = await supabase.storage.from("org-branding").createSignedUrl(path, 3600);
    setLogoPath(path);
    setLogoUrl(signed?.signedUrl ?? null);
    setUploading(false);
    toast.success("Logo uploaded — remember to save");
  };

  const onSave = async () => {
    if (!currentOrg) return;
    setSaving(true);
    const { error } = await supabase.from("org_branding").upsert({
      organization_id: currentOrg.id,
      logo_url: logoPath,
      primary_color: primary,
      accent_color: accent,
      subdomain: subdomain.trim() || null,
    }, { onConflict: "organization_id" });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Branding saved");
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <h1 className="font-display text-2xl font-bold md:text-3xl">Branding</h1>
      <p className="text-sm text-muted-foreground">Customize your organization's logo, colors, and subdomain.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className="space-y-5 rounded-xl border border-border bg-card p-5">
          <div>
            <Label>Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-border bg-background">
                {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-14 max-w-14 object-contain" /> : <span className="text-xs text-muted-foreground">none</span>}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={!canEdit || uploading} />
                <span className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-accent">
                  <Upload className="h-4 w-4" />{uploading ? "Uploading…" : "Upload logo"}
                </span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="primary">Primary color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input id="primary" type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} disabled={!canEdit} className="h-10 w-12 cursor-pointer rounded border border-border" />
                <Input value={primary} onChange={(e) => setPrimary(e.target.value)} disabled={!canEdit} />
              </div>
            </div>
            <div>
              <Label htmlFor="accent">Accent color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input id="accent" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} disabled={!canEdit} className="h-10 w-12 cursor-pointer rounded border border-border" />
                <Input value={accent} onChange={(e) => setAccent(e.target.value)} disabled={!canEdit} />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="subdomain">Custom subdomain</Label>
            <div className="mt-1 flex items-center gap-1">
              <Input id="subdomain" value={subdomain} onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="acme" disabled={!canEdit} />
              <span className="text-sm text-muted-foreground">.missionready.dev</span>
            </div>
          </div>

          <Button onClick={onSave} disabled={!canEdit || saving} className="w-full">
            {saving ? "Saving…" : "Save branding"}
          </Button>
          {!canEdit && <p className="text-xs text-muted-foreground">Only owners and admins can edit branding.</p>}
        </div>

        {/* Live preview */}
        <div className="space-y-4 rounded-xl border border-border bg-card p-5">
          <h3 className="font-display font-semibold">Live preview</h3>
          <div className="rounded-lg border border-border bg-background p-5" style={{ ["--primary" as never]: primary, ["--accent" as never]: accent }}>
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt="Preview" className="h-10 w-10 object-contain" />}
              <span className="font-display font-bold uppercase tracking-wider">{currentOrg?.name ?? "Your Org"}</span>
            </div>
            <button style={{ background: primary, color: "white" }} className="mt-4 rounded-md px-4 py-2 text-sm font-medium">Primary button</button>
            <button style={{ background: accent, color: "white" }} className="ml-2 mt-4 rounded-md px-4 py-2 text-sm font-medium">Accent button</button>
            <div className="mt-4 rounded border-l-4 p-3" style={{ borderColor: primary, background: `${primary}10` }}>
              Sample alert card with your primary color.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
